(function () {
    const SELECTORS = {
      slider: '[data-horizontal-slider]'
    };
  
    const MIN_THUMB_WIDTH_PX = 80; // Minimum thumb width in pixels for usability
    const MIN_THUMB_WIDTH_PERCENT = 0.1; // 10% minimum as fallback for very wide scrollbars
  
    const initializeAll = (root = document) => {
      const sliders = root.querySelectorAll(SELECTORS.slider);
      sliders.forEach(initializeSlider);
    };
  
    const cleanupSlider = (slider) => {
      const cleanup = slider._horizontalSliderCleanup;
      if (typeof cleanup === 'function') {
        cleanup();
      }
      slider._horizontalSliderCleanup = undefined;
      slider.dataset.horizontalSliderInitialized = 'false';
      
      // Reset scrollbar and thumb state so they can be properly recalculated
      const scrollbar = slider.querySelector('[data-horizontal-slider-scrollbar]');
      const thumb = slider.querySelector('[data-horizontal-slider-thumb]');
      
      if (scrollbar) {
        scrollbar.classList.remove('is-hidden');
      }
      
      if (thumb) {
        thumb.classList.remove('is-disabled');
        thumb.style.display = '';
        thumb.style.opacity = '';
        thumb.style.width = '';
        thumb.style.minWidth = '';
        thumb.style.transform = '';
      }
    };
  
    const initializeSlider = (slider) => {
      if (!slider || slider.dataset.horizontalSliderInitialized === 'true') {
        return;
      }
  
      const viewport = slider.querySelector('[data-horizontal-slider-viewport]');
      const track = slider.querySelector('[data-horizontal-slider-track]');
      const scrollbar = slider.querySelector('[data-horizontal-slider-scrollbar]');
      const thumb = slider.querySelector('[data-horizontal-slider-thumb]');
  
      if (!viewport || !track || !scrollbar || !thumb) {
        slider.dataset.horizontalSliderInitialized = 'true';
        return;
      }
  
      slider.dataset.horizontalSliderInitialized = 'true';
  
      const metrics = () => {
        const viewportWidth = viewport.clientWidth;
        const contentWidth = track.scrollWidth;
        const scrollbarWidth = scrollbar.clientWidth;
        const maxScrollLeft = Math.max(contentWidth - viewportWidth, 0);
        const ratio = maxScrollLeft > 0 ? viewportWidth / contentWidth : 1;
        const calculatedThumbWidth = ratio * scrollbarWidth;
        
        // Use pixel-based minimum for better consistency, with percentage fallback for very wide scrollbars
        const minThumbWidthPx = Math.max(MIN_THUMB_WIDTH_PX, scrollbarWidth * MIN_THUMB_WIDTH_PERCENT);
        const thumbWidth = Math.max(calculatedThumbWidth, minThumbWidthPx);
        const maxThumbOffset = Math.max(scrollbarWidth - thumbWidth, 0);
  
        return {
          viewportWidth,
          contentWidth,
          scrollbarWidth,
          maxScrollLeft,
          thumbWidth,
          maxThumbOffset
        };
      };
  
      const scheduleUpdate = () => {
        window.requestAnimationFrame(updateThumb);
      };
  
      const updateThumb = () => {
        const data = metrics();
        const hasOverflow = data.contentWidth > data.viewportWidth + 1;
  
        if (hasOverflow && data.scrollbarWidth > 0) {
          thumb.classList.remove('is-disabled');
          scrollbar.classList.remove('is-hidden');
          thumb.style.width = `${data.thumbWidth}px`;
          thumb.style.minWidth = '0'; // Remove fixed min-width to allow proper calculation
          const scrollLeft = Math.min(Math.max(viewport.scrollLeft, 0), data.maxScrollLeft);
          const thumbOffset = data.maxScrollLeft > 0 ? (scrollLeft / data.maxScrollLeft) * data.maxThumbOffset : 0;
          thumb.dataset.thumbOffset = thumbOffset;
          thumb.style.transform = `translate3d(${thumbOffset}px, 0, 0)`;
          thumb.style.opacity = '1';
          thumb.style.display = 'block';
        } else {
          thumb.classList.add('is-disabled');
          scrollbar.classList.add('is-hidden');
          thumb.style.width = `${data.scrollbarWidth}px`;
          thumb.style.minWidth = '0';
          thumb.dataset.thumbOffset = 0;
          thumb.style.transform = 'translate3d(0, 0, 0)';
          thumb.style.opacity = '0';
          thumb.style.display = 'none';
        }
      };
  
      const syncScroll = () => {
        scheduleUpdate();
      };
  
      const startThumbDrag = (event) => {
        if (event.button !== undefined && event.button !== 0) {
          return;
        }
  
        if (thumb.classList.contains('is-disabled')) {
          return;
        }
  
        const pointerId = event.pointerId;
        const data = metrics();
        const startX = event.clientX;
        const startOffset = parseFloat(thumb.dataset.thumbOffset || '0');
  
        const move = (moveEvent) => {
          if (pointerId !== undefined && moveEvent.pointerId !== pointerId) {
            return;
          }
  
          const delta = moveEvent.clientX - startX;
          const nextOffset = Math.min(Math.max(startOffset + delta, 0), data.maxThumbOffset);
          const scrollLeft = data.maxThumbOffset > 0 ? (nextOffset / data.maxThumbOffset) * data.maxScrollLeft : 0;
          viewport.scrollLeft = scrollLeft;
        };
  
        const end = (endEvent) => {
          if (pointerId !== undefined && endEvent.pointerId !== pointerId) {
            return;
          }
  
          document.removeEventListener('pointermove', move);
          document.removeEventListener('pointerup', end);
          document.removeEventListener('pointercancel', end);
          thumb.releasePointerCapture?.(pointerId);
        };
  
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', end);
        document.addEventListener('pointercancel', end);
        thumb.setPointerCapture?.(pointerId);
        event.preventDefault();
      };
  
      const onScrollbarPointerDown = (event) => {
        if (event.target === thumb) {
          return;
        }
  
        if (thumb.classList.contains('is-disabled')) {
          return;
        }
  
        const data = metrics();
        if (data.contentWidth <= data.viewportWidth) {
          return;
        }
  
        const rect = scrollbar.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const thumbCenter = data.maxThumbOffset > 0 ? (clickX - data.thumbWidth / 2) : 0;
        const nextOffset = Math.min(Math.max(thumbCenter, 0), data.maxThumbOffset);
        const scrollLeft = data.maxThumbOffset > 0 ? (nextOffset / data.maxThumbOffset) * data.maxScrollLeft : 0;
  
        viewport.scrollLeft = scrollLeft;
      };
  
      viewport.addEventListener('scroll', syncScroll);
      window.addEventListener('resize', syncScroll);
      window.addEventListener('load', scheduleUpdate, { once: true });
  
      thumb.addEventListener('pointerdown', startThumbDrag);
      scrollbar.addEventListener('pointerdown', onScrollbarPointerDown);
  
      let resizeObserver;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(scheduleUpdate);
        resizeObserver.observe(viewport);
        resizeObserver.observe(track);
        resizeObserver.observe(scrollbar);
      }
  
      slider._horizontalSliderCleanup = () => {
        viewport.removeEventListener('scroll', syncScroll);
        window.removeEventListener('resize', syncScroll);
        thumb.removeEventListener('pointerdown', startThumbDrag);
        scrollbar.removeEventListener('pointerdown', onScrollbarPointerDown);
        resizeObserver?.disconnect();
      };
  
      scheduleUpdate();
    };
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeAll());
    } else {
      initializeAll();
    }
  
    document.addEventListener('shopify:section:load', (event) => {
      if (event && event.target) {
        initializeAll(event.target);
      }
    });
  
    document.addEventListener('shopify:section:unload', (event) => {
      if (event && event.target) {
        const sliders = event.target.querySelectorAll(SELECTORS.slider);
        sliders.forEach((slider) => {
          cleanupSlider(slider);
        });
      }
    });
  
    // Expose functions globally for use by other scripts (e.g., tabbed slider)
    window.initializeHorizontalSlider = initializeSlider;
    window.cleanupHorizontalSlider = cleanupSlider;
  })();
  